import { Link } from 'react-router-dom'
import { Button } from '@orisini/ui/components/button'
import { Bagian, GagalMuat } from './Bagian'
import type { Rilis, Unduhan } from '../lib/data'

function ukuran(bytes: number | null): string {
  return bytes ? ` · ${(bytes / 1_048_576).toFixed(0)} MB` : ''
}

function TombolUnduh({ unduhan, label }: { unduhan: Unduhan; label: string }) {
  return (
    <Button asChild className="w-full sm:w-auto">
      <a href={unduhan.url} download>
        {label} v{unduhan.version}
      </a>
    </Button>
  )
}

/**
 * Ajakan memasang agent.
 *
 * Versinya dibaca dari manifes rilis di R2 — sama seperti halaman `/unduh`,
 * jadi tidak ada nomor versi yang ditulis tangan di sini dan tidak perlu deploy
 * ulang tiap kali agent dirilis.
 */
export function BagianUnduhan({
  rilis,
  gagal,
  ulangi,
}: {
  rilis: Rilis | null
  gagal: boolean
  ulangi: () => void
}) {
  const adaRilis = rilis !== null && (rilis.windows !== null || rilis.mac !== null)

  return (
    <Bagian
      id="download"
      judul="Agent for your computer"
      keterangan="Installed once, used for every Orisini product you're subscribed to. Once installed, the agent updates itself."
    >
      <div className="grid gap-x-12 gap-y-8 border-t border-border pt-8 lg:grid-cols-12">
        <div className="space-y-4 text-sm text-muted-foreground lg:col-span-7">
          <p>
            <strong className="font-medium text-foreground">Google Chrome must be installed.</strong>{' '}
            The agent controls real Chrome, not a bundled browser, so your platform session looks
            like everyday use.
          </p>
          <p>
            After installing, sign in once with your orisini.com account. The credentials are
            exchanged for a device token and{' '}
            <strong className="font-medium text-foreground">not stored</strong>.
          </p>
          <p>
            Leave the computer on so the queue keeps running. Installed devices can be viewed and
            revoked at any time from the devices page.
          </p>
          <p>
            Full installation notes — including the Windows SmartScreen and macOS Gatekeeper
            warnings — are on the{' '}
            <Link
              to="/download"
              className="rounded-sm text-foreground underline underline-offset-4 decoration-border hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              download page
            </Link>
            .
          </p>
        </div>

        <div className="lg:col-span-5">
          {gagal ? (
            <GagalMuat pesan="Couldn't load release information." ulangi={ulangi} />
          ) : !adaRilis ? (
            <p className="text-sm text-muted-foreground">
              {rilis === null ? 'Checking the latest release…' : 'No version has been released yet.'}
            </p>
          ) : (
            <div className="space-y-5">
              {rilis.windows && (
                <div className="space-y-2">
                  <TombolUnduh unduhan={rilis.windows} label="Windows" />
                  <p className="text-xs text-muted-foreground">
                    Windows 10 or newer{ukuran(rilis.windows.sizeBytes)}
                  </p>
                </div>
              )}

              {rilis.mac && (
                <div className="space-y-2">
                  <TombolUnduh unduhan={rilis.mac} label="macOS" />
                  <p className="text-xs text-muted-foreground">
                    macOS 12 or newer{ukuran(rilis.mac.sizeBytes)}
                    {!rilis.macAutoUpdate && " · can't update itself yet"}
                  </p>
                </div>
              )}

              {!rilis.windows && (
                <p className="text-xs text-muted-foreground">
                  The Windows version is being prepared. For now only macOS is available.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Bagian>
  )
}
